import React, { createContext, useState, useEffect } from 'react';
import axios from 'axios';
import { BASE_URL } from '../../utils/globals';

export const AuthContext = createContext();

export default function AuthProvider({ children }) {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [fullname, setFullname] = useState('');
  const [firstname, setFirstname] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);

  const [error, setError] = useState('');

  const sanitizeIdentityValue = (value) => {
    const text = String(value ?? '').trim();
    if (!text) return '';
    const lowered = text.toLowerCase();
    return lowered === 'undefined' || lowered === 'null' ? '' : text;
  };

  const extractHouseId = (user = {}) => {
    const candidates = [
      user.houseId,
      user.house_id?._id,
      user.house_id?.id,
      user.house_id,
    ];

    const valid = candidates.find((value) => {
      if (!value) return false;
      if (typeof value !== 'string') return true;
      const trimmed = value.trim();
      return trimmed !== '' && trimmed !== 'undefined' && trimmed !== '[object Object]';
    });

    return typeof valid === 'string' ? valid.trim() : (valid || '');
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    setIsLoggedIn(!!token);
    
    // Set up axios interceptor to include token in all requests
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      axios.defaults.headers.common['Cache-Control'] = 'no-cache';
    }

    // Global response interceptor — redirect to login on expired/invalid token
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error?.response?.status === 401 || error?.response?.status === 403) {
          localStorage.removeItem('token');
          localStorage.removeItem('fullname');
          localStorage.removeItem('firstname');
          localStorage.removeItem('title');
          localStorage.removeItem('user_id');
          localStorage.removeItem('estate_id');
          localStorage.removeItem('role');
          delete axios.defaults.headers.common['Authorization'];
          setIsLoggedIn(false);
          window.location.href = '/';
        }
        return Promise.reject(error);
      }
    );

    return () => axios.interceptors.response.eject(interceptor);
  }, []);
  

  const runLogin = async (email, password) => {
    try {
      const response = await axios.post(`${BASE_URL}/auth/login`, { email, password });
      return { data: response.data, error: null };
    } catch (err) {
      console.error('Login error:', err?.response || err.message || err);
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.response?.statusText ||
        err?.message ||
        'Login failed. Please check your credentials.';
      return { data: null, error: message };
    }
  };
  

  const login = async (email, password) => {
    const { data, error } = await runLogin(email, password);
  
    if (data) {
      const safeFirstname = sanitizeIdentityValue(data?.user?.firstname);
      const safeLastname = sanitizeIdentityValue(data?.user?.lastname);
      const safeFullname =
        sanitizeIdentityValue(data?.user?.fullname) ||
        [safeFirstname, safeLastname].filter(Boolean).join(' ').trim();
      const safeTitle = sanitizeIdentityValue(data?.user?.title);

      localStorage.setItem('token', data.access_token);
      localStorage.setItem('fullname', safeFullname);
      localStorage.setItem('firstname', safeFirstname);
      localStorage.setItem('title', safeTitle);
      localStorage.setItem('user_id', data.user._id);
      localStorage.setItem('estate_id', data.user.estate_id || '');
      localStorage.setItem('house_id', extractHouseId(data.user));
      localStorage.setItem('role', data.role);
      
      console.log('AuthContext Login - User data:', data.user);
      console.log('Stored house_id:', localStorage.getItem('house_id'));

      // Set axios default header for future requests
      axios.defaults.headers.common['Authorization'] = `Bearer ${data.access_token}`;
      axios.defaults.headers.common['Cache-Control'] = 'no-cache';

      localStorage.removeItem("password");
  
      setIsLoggedIn(true);
      setFullname(data.user.fullname);
      setFirstname(data.user.firstname);
      setIsAdmin(data.role === 'ADMIN' || data.role === 'SUPER ADMIN'); 
  
      setError('');
    } else {
      setError(error);
    }
    return { data, error };
  };
  

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('fullname');
    localStorage.removeItem('firstname');
    localStorage.removeItem('title');
    localStorage.removeItem('user_id');
    localStorage.removeItem('house_id');
    localStorage.removeItem('estate_id');
    localStorage.removeItem('role');
    localStorage.removeItem('combinedData');

    // Remove axios authorization header
    delete axios.defaults.headers.common['Authorization'];

    setIsLoggedIn(false);
    setIsAdmin(false);
    setFullname('');
    setFirstname('');
  };

  return (
    <AuthContext.Provider
      value={{
        isLoggedIn,
        fullname,
        firstname,
        login,
        logout,
        error,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}